import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip,
    Autocomplete,
    CircularProgress,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    alpha,
    Menu,
    Divider,
    useMediaQuery,
    useTheme,
    Collapse,
    Badge
} from '@mui/material';
import {
    Upload as UploadIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    ArrowUpward,
    ArrowDownward,
    UnfoldMore,
    FilterList as FilterListIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Refresh as RefreshIcon,
    FilePresent as FileIcon,
    CloudUpload as CloudUploadIcon,
    Download as DownloadIcon,
    ViewColumn as ViewColumnIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useMap } from 'react-leaflet';
import { addNotification } from '../../store/notificationSlice';
import {
    uploadPautinaFile,
    getPautinaFiles,
    getPautinaFileData,
    deletePautinaFile,
    downloadPautinaFile
} from '../../http/pautinaApi';

// Вспомогательные функции для стилей
const getActionButtonSx = (btncolor = 'primary') => ({
    backgroundColor: 'var(--background-paper)',
    border: '1px solid var(--border-default)',
    borderRadius: { xs: '4px', md: '6px' },
    padding: { xs: '2px', md: '6px' },
    transition: 'all 0.15s ease',
    color: btncolor === 'error' ? 'var(--error-color)' :
        btncolor === 'success' ? 'var(--success-color)' :
            'var(--text-secondary)',
    '&:hover': {
        backgroundColor: btncolor === 'error' ? 'var(--error-bg)' :
            btncolor === 'success' ? 'var(--success-bg)' :
                'var(--hover-bg)',
        borderColor: btncolor === 'error' ? 'var(--error-color)' :
            btncolor === 'success' ? 'var(--success-color)' :
                'var(--primary-color)',
        color: btncolor === 'error' ? 'var(--error-color)' :
            btncolor === 'success' ? 'var(--success-color)' :
                'var(--primary-color)',
    },
    '&:disabled': {
        opacity: 0.4,
        cursor: 'not-allowed',
    },
    '& .MuiSvgIcon-root': {
        fontSize: { xs: '16px', md: '20px' },
    },
});

const getFilterChipSx = (active) => ({
    backgroundColor: active ? 'var(--primary-color)' : 'transparent',
    color: active ? '#fff' : 'var(--text-secondary)',
    border: active ? 'none' : '1px solid var(--border-default)',
    transition: 'all 0.15s ease',
    cursor: 'pointer',
    fontSize: { xs: '10px', md: '11px' },
    height: { xs: 24, md: 28 },
    '&:hover': {
        backgroundColor: active ? 'var(--primary-dark)' : 'var(--hover-bg)',
    },
    '& .MuiChip-icon': {
        fontSize: { xs: '12px', md: '14px' },
        marginLeft: { xs: '4px', md: '6px' },
        marginRight: { xs: '-2px', md: '0' },
    },
    '& .MuiChip-label': {
        padding: { xs: '0 6px', md: '0 8px' },
    },
});

const getStatusChipSx = (status) => {
    const colors = {
        success: { bg: 'var(--success-bg)', text: 'var(--success-color)' },
        warning: { bg: 'var(--warning-bg)', text: 'var(--warning-color)' },
        error: { bg: 'var(--error-bg)', text: 'var(--error-color)' },
        info: { bg: 'var(--info-bg)', text: 'var(--info-color)' },
        default: { bg: 'var(--hover-bg)', text: 'var(--text-secondary)' },
    };
    const { bg, text } = colors[status] || colors.default;
    return {
        backgroundColor: bg,
        color: text,
        fontSize: { xs: '8px', md: '11px' },
        height: { xs: 18, md: 22 },
        fontWeight: 600,
        border: 'none',
        '& .MuiChip-icon': {
            fontSize: { xs: '10px', md: '14px' },
            marginLeft: { xs: '2px', md: '4px' },
            marginRight: { xs: '-2px', md: '0' },
        },
        '& .MuiChip-label': {
            padding: { xs: '0 4px', md: '0 8px' },
        },
    };
};

// Компонент заголовка с сортировкой
const SortableHeader = ({ column, sortConfig, onSort }) => {
    const isActive = sortConfig.key === column.key;

    return (
        <TableCell
            onClick={() => column.sortable !== false && onSort(column.key)}
            sx={{
                cursor: column.sortable !== false ? 'pointer' : 'default',
                userSelect: 'none',
                minWidth: column.width || 'auto',
                textAlign: 'center',
                '&:hover': {
                    backgroundColor: column.sortable !== false ? 'var(--divider)' : 'transparent',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.4, md: 0.5 }, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: { xs: '9px', md: '11px' }, fontWeight: 600 }}>
                    {column.label}
                </Typography>
                {column.sortable !== false && (
                    isActive ? (
                        sortConfig.direction === 'asc' ?
                            <ArrowUpward sx={{ fontSize: { xs: 12, md: 14 } }} /> :
                            <ArrowDownward sx={{ fontSize: { xs: 12, md: 14 } }} />
                    ) : (
                        <UnfoldMore sx={{ fontSize: { xs: 12, md: 14 }, opacity: 0.5 }} />
                    )
                )}
            </Box>
        </TableCell>
    );
};

// Компонент для форматированного отображения расписания
const FormattedScheduleText = ({ text }) => {
    if (!text) return <Typography sx={{ fontSize: { xs: '9px', md: '11px' }, color: 'var(--text-secondary)', fontStyle: 'italic' }}>Нет данных</Typography>;

    const lines = text.split('\n');
    return (
        <Box sx={{ whiteSpace: 'pre-line' }}>
            {lines.map((line, index) => {
                const isBold = line.startsWith('**') && line.endsWith('**');
                const cleanLine = isBold ? line.slice(2, -2) : line;
                return (
                    <Box
                        key={index}
                        component="span"
                        sx={{
                            fontWeight: isBold ? 600 : 'normal',
                            color: isBold ? 'var(--warning-color)' : 'inherit',
                            display: 'block',
                            fontSize: { xs: '9px', md: '11px' },
                        }}
                    >
                        {cleanLine}
                    </Box>
                );
            })}
        </Box>
    );
};

// Утилиты валидации
const validatePoverkaDate = (dateString) => {
    if (!dateString) return { isValid: false, status: 'no-date' };
    try {
        const [day, month, year] = dateString.split('.');
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return { isValid: false, status: 'invalid' };

        const today = new Date();
        const diffTime = date.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { isValid: false, status: 'expired', daysLeft: diffDays };
        if (diffDays <= 30) return { isValid: true, status: 'warning', daysLeft: diffDays };
        return { isValid: true, status: 'valid', daysLeft: diffDays };
    } catch {
        return { isValid: false, status: 'invalid' };
    }
};

const validateStatus = (statusText) => {
    if (!statusText) return { isValid: false, status: 'no-data' };
    return statusText.toLowerCase().includes('утвержден')
        ? { isValid: true, status: 'approved' }
        : { isValid: false, status: 'not-approved' };
};

const validatePerformance = (performanceText) => {
    if (!performanceText) return { isValid: false, status: 'no-data' };
    return performanceText.trim() === 'Работает без ограничений'
        ? { isValid: true, status: 'working' }
        : { isValid: false, status: 'not-working' };
};

const validateApprovedViolations = (violationsText) => {
    if (!violationsText || violationsText.trim() === '') return { isValid: false, status: 'empty' };
    return { isValid: true, status: 'has-violations' };
};

const formatSchedule = (scheduleText) => {
    if (!scheduleText || scheduleText.trim() === '') return { formatted: 'Нет данных', isCurrent: false };

    try {
        const records = scheduleText.split('; ;').filter(r => r.trim());
        if (records.length === 0) return { formatted: 'Нет данных', isCurrent: false };

        let hasCurrentRecord = false;
        const formattedRecords = [];
        const uniqueRecords = new Set();

        records.forEach(record => {
            const trimmed = record.trim();
            if (!trimmed) return;

            const parts = trimmed.split(';');
            if (parts.length < 2) return;

            const violationPart = parts[0].trim();
            const daysPart = parts[1].trim();

            const violationMatch = violationPart.match(/^(\d+\.\s*[^,]+),\s*(.+)$/);
            if (!violationMatch) return;

            const violationName = violationMatch[1].replace(/^\d+\.\s*/, '');
            const timeInfo = violationMatch[2];

            const daysMatch = daysPart.match(/Дни недели:\s*(.+)/);
            const days = daysMatch ? daysMatch[1].trim() : 'Не указаны';

            let timeRange = timeInfo;
            let isCurrent = false;

            if (timeInfo.includes(' - ')) {
                const timeMatch = timeInfo.match(/(\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2})\s+-\s+(.+)/);
                if (timeMatch) {
                    const startTime = timeMatch[1];
                    const endTime = timeMatch[2];
                    timeRange = `${startTime} - ${endTime}`;

                    try {
                        const [startDate, startTimeOnly] = startTime.split(' ');
                        const [startDay, startMonth, startYear] = startDate.split('.');
                        const [startHours, startMinutes] = startTimeOnly.split(':');

                        const startDateTime = new Date(startYear, startMonth - 1, startDay, startHours, startMinutes);
                        const now = new Date();

                        if (startDateTime <= now) {
                            if (endTime.includes('[*]')) {
                                isCurrent = true;
                                hasCurrentRecord = true;
                            } else {
                                const endTimeMatch = endTime.match(/(\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2})/);
                                if (endTimeMatch) {
                                    const [endDate, endTimeOnly] = endTimeMatch[1].split(' ');
                                    const [endDay, endMonth, endYear] = endDate.split('.');
                                    const [endHours, endMinutes] = endTimeOnly.split(':');
                                    const endDateTime = new Date(endYear, endMonth - 1, endDay, endHours, endMinutes);
                                    if (now <= endDateTime) {
                                        isCurrent = true;
                                        hasCurrentRecord = true;
                                    }
                                }
                            }
                        }
                    } catch { }
                }
            }

            const uniqueKey = `${violationName}|${timeRange}|${days}`;
            if (!uniqueRecords.has(uniqueKey)) {
                uniqueRecords.add(uniqueKey);
                formattedRecords.push({ violation: violationName, timeRange, days, isCurrent });
            }
        });

        const formattedText = formattedRecords.map(r => {
            const v = r.isCurrent ? `**${r.violation}**` : r.violation;
            const t = r.isCurrent ? `**Время: ${r.timeRange}**` : `Время: ${r.timeRange}`;
            const d = r.isCurrent ? `**Дни: ${r.days}**` : `Дни: ${r.days}`;
            return `${v}\n${t}\n${d}`;
        }).join('\n\n');

        return { formatted: formattedText, isCurrent: hasCurrentRecord, recordCount: formattedRecords.length };
    } catch {
        return { formatted: scheduleText, isCurrent: false };
    }
};

// Определение колонок
const columns = [
    { key: 'id', label: 'ID', width: 60 },
    { key: 'address', label: 'Адрес', width: 200 },
    { key: 'complexName', label: 'Класс', width: 150 },
    { key: 'complex', label: 'Серийный номер', width: 120 },
    { key: 'tag', label: 'Тэг', width: 100 },
    { key: 'violations', label: 'Нарушения', width: 80 },
    { key: 'status', label: 'Состояние', width: 120 },
    { key: 'poverka', label: 'Номер сертификата', width: 130 },
    { key: 'poverka_from', label: 'Поверка от', width: 140 },
    { key: 'poverka_to', label: 'Поверка до', width: 140 },
    { key: 'performance', label: 'Работоспособность', width: 150 },
    { key: 'cause', label: 'Причины', width: 150 },
    { key: 'approved_vilations', label: 'Утв. нарушения', width: 180 },
    { key: 'proposed_vilations', label: 'Предл. нарушения', width: 180 },
    { key: 'rejected_vilations', label: 'Откл. нарушения', width: 180 },
    { key: 'excluded_violations', label: 'Искл. нарушения', width: 180 },
    { key: 'speed', label: 'Скорость', width: 80 },
    { key: 'schedule', label: 'Расписание', width: 280 },
];

// Типы проблем для фильтрации
const problemFilters = [
    { key: 'expired', label: 'Истекшая поверка', icon: ErrorIcon, color: 'error' },
    { key: 'expiring', label: 'Истекает поверка', icon: WarningIcon, color: 'warning' },
    { key: 'notApproved', label: 'Не утвержден', icon: WarningIcon, color: 'warning' },
    { key: 'notWorking', label: 'Не работает', icon: ErrorIcon, color: 'error' },
    { key: 'emptyViolations', label: 'Нет нарушений', icon: WarningIcon, color: 'warning' },
    { key: 'activeSchedule', label: 'Активное расписание', icon: ScheduleIcon, color: 'info' },
];

const PautinaDialog = () => {
    const map = useMap();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fileInputRef = useRef(null);
    const user = useSelector(state => state.store.user);
    const isAdmin = user?.roles?.includes('admin');

    // Ключи для localStorage
    const STORAGE_KEY = 'pautinaDialogState';

    // Функция для загрузки состояния из localStorage
    const loadStateFromStorage = useCallback(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (error) {
            console.error('Ошибка загрузки состояния из localStorage:', error);
        }
        return null;
    }, []);

    // Функция для сохранения состояния в localStorage
    const saveStateToStorage = useCallback((state) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (error) {
            console.error('Ошибка сохранения состояния в localStorage:', error);
        }
    }, []);

    // Восстановление состояния из localStorage
    const savedState = useMemo(() => loadStateFromStorage(), [loadStateFromStorage]);

    // Состояния
    const [files, setFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileData, setFileData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Фильтры и сортировка с восстановлением из localStorage
    const [searchTerm, setSearchTerm] = useState(savedState?.searchTerm || '');
    const [sortConfig, setSortConfig] = useState(savedState?.sortConfig || { key: null, direction: 'asc' });
    const [selectedProblems, setSelectedProblems] = useState(savedState?.selectedProblems || []);
    const [selectedTags, setSelectedTags] = useState(savedState?.selectedTags || []);
    const [selectedClasses, setSelectedClasses] = useState(savedState?.selectedClasses || []);

    // Видимость столбцов с восстановлением из localStorage
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const defaultVisible = columns.reduce((acc, col) => {
            acc[col.key] = true;
            return acc;
        }, {});

        if (savedState?.visibleColumns) {
            // Объединяем сохраненные настройки с настройками по умолчанию для новых столбцов
            return columns.reduce((acc, col) => {
                acc[col.key] = savedState.visibleColumns[col.key] !== undefined
                    ? savedState.visibleColumns[col.key]
                    : true;
                return acc;
            }, {});
        }

        return defaultVisible;
    });

    // Меню управления столбцами
    const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(false); // Состояние сворачивания фильтров на мобильных

    // Загрузка данных файла
    const loadFileData = useCallback(async (fileId) => {
        try {
            setLoading(true);
            const response = await getPautinaFileData(fileId);
            const rows = response.data.rows || [];

            // Маппинг данных с правильными полями
            const mapped = rows.map(r => ({
                id: r.id,
                address: r.complex?.address || '',
                complexName: r.complex?.class || '',
                complex: r.complex?.sn || '',
                tag: r.complex?.tag || '',
                ...r.data,
            }));

            setFileData(mapped);
        } catch (error) {
            dispatch(addNotification({
                message: 'Ошибка загрузки данных файла',
                options: { variant: 'error' }
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    // Загрузка списка файлов
    const loadFiles = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getPautinaFiles();
            const filesList = response.data.files || [];
            setFiles(filesList);

            // Восстанавливаем выбранный файл из localStorage или выбираем последний
            if (filesList.length > 0 && !selectedFile) {
                let fileToSelect = null;

                // Пытаемся восстановить сохраненный файл по ID
                const savedState = loadStateFromStorage();
                if (savedState?.selectedFileId) {
                    fileToSelect = filesList.find(f => f.id === savedState.selectedFileId);
                }

                // Если сохраненный файл не найден, выбираем последний
                if (!fileToSelect) {
                    fileToSelect = filesList[0];
                }

                setSelectedFile(fileToSelect);
                await loadFileData(fileToSelect.id);
            }
        } catch (error) {
            dispatch(addNotification({
                message: 'Ошибка загрузки списка файлов',
                options: { variant: 'error' }
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, selectedFile, loadStateFromStorage, loadFileData]);

    useEffect(() => {
        loadFiles();
    }, []);

    // Сохранение состояния в localStorage при изменении фильтров
    useEffect(() => {
        if (selectedFile) {
            saveStateToStorage({
                selectedFileId: selectedFile.id,
                searchTerm,
                sortConfig,
                selectedProblems,
                selectedTags,
                selectedClasses,
                visibleColumns
            });
        }
    }, [selectedFile, searchTerm, sortConfig, selectedProblems, selectedTags, selectedClasses, visibleColumns, saveStateToStorage]);

    // Обработчик выбора файла
    const handleFileSelect = useCallback(async (event, newValue) => {
        if (newValue) {
            setSelectedFile(newValue);
            await loadFileData(newValue.id);
            // Сохраняем выбранный файл в localStorage
            saveStateToStorage({
                selectedFileId: newValue.id,
                searchTerm,
                sortConfig,
                selectedProblems,
                selectedTags,
                selectedClasses
            });
        }
    }, [loadFileData, searchTerm, sortConfig, selectedProblems, selectedTags, selectedClasses]);

    // Загрузка файла
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx')) {
            dispatch(addNotification({
                message: 'Поддерживаются только файлы Excel (.xlsx)',
                options: { variant: 'error' }
            }));
            return;
        }

        try {
            setUploading(true);
            await uploadPautinaFile(file);
            dispatch(addNotification({
                message: 'Файл успешно загружен',
                options: { variant: 'success' }
            }));
            await loadFiles();
            event.target.value = '';
        } catch (error) {
            dispatch(addNotification({
                message: error.message || 'Ошибка загрузки файла',
                options: { variant: 'error' }
            }));
        } finally {
            setUploading(false);
        }
    };

    // Скачивание файла
    const handleFileDownload = async () => {
        if (!selectedFile || !selectedFile.id) return;

        try {
            const response = await downloadPautinaFile(selectedFile.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            // Используем оригинальное имя файла или имя из БД
            link.setAttribute('download', selectedFile.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            dispatch(addNotification({
                message: 'Файл успешно скачан',
                options: { variant: 'success' }
            }));
        } catch (error) {
            dispatch(addNotification({
                message: error.response?.data?.message || 'Ошибка скачивания файла',
                options: { variant: 'error' }
            }));
        }
    };

    // Удаление файла
    const handleFileDelete = async () => {
        if (!selectedFile || !selectedFile.id) return;
        if (!window.confirm(`Удалить файл "${selectedFile.name}"?`)) return;

        try {
            setDeleting(true);
            await deletePautinaFile(selectedFile.id);
            dispatch(addNotification({
                message: 'Файл успешно удален',
                options: { variant: 'success' }
            }));
            setSelectedFile(null);
            setFileData([]);
            await loadFiles();
        } catch (error) {
            console.error('Ошибка удаления файла:', error);
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Ошибка удаления файла';
            dispatch(addNotification({
                message: errorMessage,
                options: { variant: 'error' }
            }));
        } finally {
            setDeleting(false);
        }
    };

    // Сортировка
    const handleSort = useCallback((key) => {
        setSortConfig(prev => {
            const newConfig = {
                key,
                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
            };
            // Сохраняем в localStorage
            if (selectedFile) {
                saveStateToStorage({
                    selectedFileId: selectedFile.id,
                    searchTerm,
                    sortConfig: newConfig,
                    selectedProblems,
                    selectedTags,
                    selectedClasses,
                    visibleColumns
                });
            }
            return newConfig;
        });
    }, [selectedFile, searchTerm, selectedProblems, selectedTags, selectedClasses, visibleColumns, saveStateToStorage]);

    // Обработчик изменения видимости столбца
    const handleColumnVisibilityChange = useCallback((columnKey) => {
        setVisibleColumns(prev => {
            const newVisible = {
                ...prev,
                [columnKey]: !prev[columnKey]
            };
            // Сохраняем в localStorage
            if (selectedFile) {
                saveStateToStorage({
                    selectedFileId: selectedFile.id,
                    searchTerm,
                    sortConfig,
                    selectedProblems,
                    selectedTags,
                    selectedClasses,
                    visibleColumns: newVisible
                });
            }
            return newVisible;
        });
    }, [selectedFile, searchTerm, sortConfig, selectedProblems, selectedTags, selectedClasses, saveStateToStorage]);

    // Показать все столбцы
    const handleShowAllColumns = useCallback(() => {
        const allVisible = columns.reduce((acc, col) => {
            acc[col.key] = true;
            return acc;
        }, {});
        setVisibleColumns(allVisible);
        if (selectedFile) {
            saveStateToStorage({
                selectedFileId: selectedFile.id,
                searchTerm,
                sortConfig,
                selectedProblems,
                selectedTags,
                selectedClasses,
                visibleColumns: allVisible
            });
        }
    }, [selectedFile, searchTerm, sortConfig, selectedProblems, selectedTags, selectedClasses, saveStateToStorage]);

    // Скрыть все столбцы (кроме ID)
    const handleHideAllColumns = useCallback(() => {
        const allHidden = columns.reduce((acc, col) => {
            acc[col.key] = col.key === 'id'; // Оставляем только ID видимым
            return acc;
        }, {});
        setVisibleColumns(allHidden);
        if (selectedFile) {
            saveStateToStorage({
                selectedFileId: selectedFile.id,
                searchTerm,
                sortConfig,
                selectedProblems,
                selectedTags,
                selectedClasses,
                visibleColumns: allHidden
            });
        }
    }, [selectedFile, searchTerm, sortConfig, selectedProblems, selectedTags, selectedClasses, saveStateToStorage]);

    // Отфильтрованные видимые столбцы
    const visibleColumnsList = useMemo(() => {
        return columns.filter(col => visibleColumns[col.key] !== false);
    }, [visibleColumns]);

    // Получение уникальных тэгов и классов
    const uniqueTags = useMemo(() => {
        const tags = new Set(fileData.map(item => item.tag).filter(Boolean));
        return Array.from(tags).sort();
    }, [fileData]);

    const uniqueClasses = useMemo(() => {
        const classes = new Set(fileData.map(item => item.complexName).filter(Boolean));
        return Array.from(classes).sort();
    }, [fileData]);

    // Фильтрация и сортировка данных
    const processedData = useMemo(() => {
        let result = [...fileData];

        // Поиск по тексту
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            result = result.filter(item =>
                Object.values(item).some(val =>
                    val && String(val).toLowerCase().includes(search)
                )
            );
        }

        // Фильтр по тэгам
        if (selectedTags.length > 0) {
            result = result.filter(item => selectedTags.includes(item.tag));
        }

        // Фильтр по классам
        if (selectedClasses.length > 0) {
            result = result.filter(item => selectedClasses.includes(item.complexName));
        }

        // Фильтр по проблемам
        if (selectedProblems.length > 0) {
            result = result.filter(item => {
                return selectedProblems.some(problem => {
                    switch (problem) {
                        case 'expired':
                            return validatePoverkaDate(item.poverka_to).status === 'expired';
                        case 'expiring':
                            return validatePoverkaDate(item.poverka_to).status === 'warning';
                        case 'notApproved':
                            return !validateStatus(item.status).isValid;
                        case 'notWorking':
                            return !validatePerformance(item.performance).isValid;
                        case 'emptyViolations':
                            return !validateApprovedViolations(item.approved_vilations).isValid;
                        case 'activeSchedule':
                            return formatSchedule(item.schedule).isCurrent;
                        default:
                            return false;
                    }
                });
            });
        }

        // Сортировка
        if (sortConfig.key) {
            result.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                }

                aVal = String(aVal || '').toLowerCase();
                bVal = String(bVal || '').toLowerCase();

                return sortConfig.direction === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            });
        }

        return result;
    }, [fileData, searchTerm, selectedTags, selectedClasses, selectedProblems, sortConfig]);

    // Статистика
    const stats = useMemo(() => {
        const s = {
            total: processedData.length,
            expired: 0,
            expiring: 0,
            notApproved: 0,
            notWorking: 0,
            emptyViolations: 0,
            activeSchedule: 0,
        };

        processedData.forEach(item => {
            const poverka = validatePoverkaDate(item.poverka_to);
            if (poverka.status === 'expired') s.expired++;
            if (poverka.status === 'warning') s.expiring++;
            if (!validateStatus(item.status).isValid) s.notApproved++;
            if (!validatePerformance(item.performance).isValid) s.notWorking++;
            if (!validateApprovedViolations(item.approved_vilations).isValid) s.emptyViolations++;
            if (formatSchedule(item.schedule).isCurrent) s.activeSchedule++;
        });

        return s;
    }, [processedData]);

    const handleClose = () => map.fire('modalclose');

    // Форматирование даты файла
    const formatFileDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ru-RU');
    };

    // Рендер ячейки
    const renderCell = (item, column) => {
        const value = item[column.key];

        // Стили для подсветки текста
        const getTextStyle = (status) => {
            const styles = {
                success: { color: 'var(--success-color)', fontWeight: 600 },
                warning: { color: 'var(--warning-color)', fontWeight: 600 },
                error: { color: 'var(--error-color)', fontWeight: 600 },
                info: { color: 'var(--info-color)', fontWeight: 600 },
                default: { color: 'var(--text-primary)' },
            };
            return styles[status] || styles.default;
        };

        switch (column.key) {
            case 'poverka_from':
                return value ? (
                    <Typography sx={{ fontSize: '11px', color: 'var(--text-primary)' }}>
                        {value}
                    </Typography>
                ) : '-';

            case 'poverka_to': {
                const validation = validatePoverkaDate(value);
                const statusMap = {
                    expired: 'error',
                    warning: 'warning',
                    valid: 'success',
                };
                const labelMap = {
                    expired: `(истекла ${Math.abs(validation.daysLeft)} дн. назад)`,
                    warning: `(осталось ${validation.daysLeft} дн.)`,
                    valid: `(действ. ${validation.daysLeft} дн.)`,
                };
                const status = statusMap[validation.status];
                return value ? (
                    <Box>
                        <Typography sx={{ fontSize: '11px', ...getTextStyle(status) }}>
                            {value}
                        </Typography>
                        {validation.status !== 'no-date' && validation.status !== 'invalid' && (
                            <Typography sx={{ fontSize: '10px', ...getTextStyle(status), opacity: 0.85 }}>
                                {labelMap[validation.status]}
                            </Typography>
                        )}
                    </Box>
                ) : '-';
            }

            case 'status': {
                const validation = validateStatus(value);
                const status = validation.isValid ? 'success' : 'warning';
                return value ? (
                    <Typography sx={{ fontSize: '11px', ...getTextStyle(status) }}>
                        {value}
                    </Typography>
                ) : '-';
            }

            case 'performance': {
                const validation = validatePerformance(value);
                const status = validation.isValid ? 'success' : 'warning';
                return value ? (
                    <Typography sx={{ fontSize: '11px', ...getTextStyle(status) }}>
                        {value}
                    </Typography>
                ) : '-';
            }

            case 'approved_vilations': {
                const validation = validateApprovedViolations(value);
                const status = validation.isValid ? 'success' : 'warning';
                return (
                    <Typography sx={{ fontSize: '11px', whiteSpace: 'pre-wrap', ...getTextStyle(status) }}>
                        {value || 'Нет данных'}
                    </Typography>
                );
            }

            case 'schedule': {
                const formatted = formatSchedule(value);
                return (
                    <Box sx={{
                        borderLeft: formatted.isCurrent ? '3px solid var(--warning-color)' : 'none',
                        paddingLeft: formatted.isCurrent ? '8px' : 0,
                    }}>
                        <FormattedScheduleText text={formatted.formatted} />
                    </Box>
                );
            }

            case 'tag':
                return value ? (
                    <Typography sx={{ fontSize: '11px', ...getTextStyle('info') }}>
                        {value}
                    </Typography>
                ) : '-';

            default:
                return value || '-';
        }
    };

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            maxWidth={false}
            fullWidth
            fullScreen
            PaperProps={{
                sx: {
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--text-primary)',
                    borderRadius: { xs: 0, md: 0 },
                    border: { xs: 'none', md: '1px solid var(--border-default)' },
                    boxShadow: { xs: 'none', md: 'var(--shadow-lg)' },
                    margin: { xs: 0, md: 0 },
                    maxHeight: { xs: '100%', md: '100%' },
                    height: { xs: '100%', md: '100%' },
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: { xs: '2px 12px', md: '4px 16px' },
                backgroundColor: 'var(--background-default)',
                borderBottom: '1px solid var(--border-default)',
                gap: { xs: '16px', md: '8px' },
                flexWrap: 'wrap',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        borderRadius: 'var(--border-radius-medium)',
                        backgroundColor: 'var(--primary-dark)',
                        flexShrink: 0,
                        '& svg': {
                            width: { xs: '18px', md: '20px' },
                            height: { xs: '18px', md: '20px' }
                        }
                    }}>
                        <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                            <path fill='white' d="M20.45 18.484L77.59 90.588C74.865 139.18 53.842 176.38 25.2 210.62l-6.716-1.76v19.32l2.557.672c15.35 45.065 9.088 90.827 1.82 138.294l-4.376 2.047v20.63l9.18-4.294c6.516 10.707 13.658 27.047 19.06 41.33 4.283 11.33 7.474 20.984 9.253 26.535l-37.493 37.69v2.43h23.942l28.55-28.7c9.27-.363 41.77-1.473 82.7-.75 46.744.825 102.612 4.3 139.05 13.667l5.08 15.784h19.632l-7.43-23.088c35.94-41.51 71.912-80.525 131.734-102.34l51.774 20.486v-20.097l-42.983-17.008c-6.374-39.21 12.755-76.665 30.62-116.68l12.363-2.585v-19.092l-6.323 1.323-5.45-2.803C425.656 182.8 405.41 133.64 388.444 78.93l44.184-60.446h-23.15l-38.6 52.81c-58.273 1.254-112.475-2.463-154.446-37.79l-2.274-15.02h-18.9l2.838 18.75C170.96 66.224 140.532 85.188 93.512 80.59l-49.22-62.106H20.448zm199.917 41.004c40.608 26.304 88.498 30.894 136.754 30.63l-33.14 45.34c-36.588.396-69.856-3.034-95.845-24.638l-7.768-51.332zm-18.834.44l8.29 54.775c-17.815 18.616-37.294 30.18-67.995 26.854L108.752 99.82c38.593-.74 68.358-17.144 92.78-39.892zM374.857 97.52c15.536 46.967 37.05 92.69 84.55 122.722l-51.768 10.82-.816-.417c-37.27-19.145-50.445-51.64-61.935-88.83l-.716-2.315 30.683-41.98zm-280.74 13.918l34.393 43.398c-2.523 29.583-15.404 52.66-33.14 74.207L44.966 215.81c23.714-29.774 42.64-63.393 49.152-104.373zm137.848 24.69c23.936 13.694 51.05 17.4 78.404 17.948l-32.91 45.028c-15.058-6.364-26.137-17.753-38.57-30.325l-2.343-2.37-4.582-30.28zm-18.778.81l5.592 36.955c-11.176 7.5-24.436 12.15-39.046 15.492l-22.902-28.897c22.71-1.395 41.095-10.68 56.356-23.55zm117.38 21.173c9.746 29.155 23.146 58.017 50.58 78.49l-55.928 11.69c-12.997-11.182-19.977-24.734-27.156-39.89l-1.67-3.53 34.174-46.76zm-186.432 16.443l19.142 24.154-.078.71c-1.93 17.365-8.877 29.63-16.65 43.063l-31.536-8.28c13.212-17.356 23.94-36.828 29.12-59.647zm77.674 19.353l6.012 39.742c-3.617.69-7.056 1.875-10.25 3.48l-25.177-31.767c10.19-2.75 20.13-6.345 29.414-11.454zm19.36 3.043c7.355 6.578 15.52 12.722 25.152 17.39l-15.834 21.662c-1.176-.493-2.377-.94-3.607-1.318l-5.71-37.735zm-62.75 20.864l24.91 31.43c-1.593 2.197-2.96 4.566-4.08 7.07l-33.914-8.902c4.955-8.757 9.83-18.253 13.084-29.598zm105.525 4.082c5.12 10.415 11.104 21.095 19.653 30.913l-31.143 6.508c-1.58-4.496-3.943-8.624-6.937-12.21l18.427-25.212zM42.172 234.4l48.873 12.83c9.227 28.215 5.795 57.08 1.2 87.448L43.173 357.64c5.95-40.147 10.29-81.414-1-123.24zm416.142 5.162c-14.933 32.635-30.114 66.733-27.433 104.13l-43.74-17.307c-3.6-25.016 8.776-49.445 20.725-76.278l50.45-10.544zm-346.576 13.1l27.614 7.248.562 1.4c6.1 15.133 5.383 31.772 2.9 49.708l-30.318 14.185c3.214-23.512 4.976-47.812-.758-72.54zm273.295 2.217c-9.043 19.817-17.797 40.803-17.252 63.847l-36.587-14.477c-.004-13.148 1.395-22.84 9.12-33.92l4.98-7.144 39.74-8.307zm-224.36 10.628l35.237 9.252c.22 3.636.94 7.135 2.078 10.44l-35.27 16.503c1.05-11.9.927-24.048-2.044-36.195zm159.16 3c-4.625 9.433-6.38 18.89-6.948 28.498l-39.278-15.54c.266-1.125.47-2.273.64-3.43l45.585-9.528zm-54.796 29.662l36.504 14.443c-8.91 4.58-17.316 10.536-24.677 16.16-3.907 2.986-7.107 5.602-9.886 7.942l-10.102-31.397c3.04-2 5.786-4.408 8.162-7.148zm-58.12 3.484L169.26 339.51c-2.558-6.2-6.018-11.147-9.588-15.235l.088-.556 47.154-22.066zm17.73 8.672c3.377.94 6.93 1.455 10.607 1.455 1.35 0 2.686-.067 4.002-.2l10.473 32.54c-13.314-.16-37.613.076-61.693 3.01l36.61-36.804zm103.486 12.81l25.363 10.034c-29.4 14.92-50.376 35.896-69.252 57.2L273.2 356.05c3.554-3.174 8.73-7.63 15.01-12.427 11.42-8.727 26.208-17.7 35.685-19.627l4.238-.86zM141.635 332.2l1.517 1.525c5.848 5.875 9.973 10.303 11.223 20.75l-31.143 31.308c-.35-2.27-.738-4.63-1.2-7.142-2.01-10.95-4.448-22.942-9.325-32.904l28.928-13.535zm235.928 10.493l39.218 15.518c-49.423 22.5-82.915 56.684-113.45 91.47l-12.435-38.647c24.103-27.838 47.673-53.616 86.668-68.34zM95.577 353.75c3.002 6.837 6.222 18.17 8.076 28.266 1.582 8.61 2.5 16.08 2.98 20.45l-35.882 36.075c-1.68-4.98-3.777-10.96-6.55-18.29-5.374-14.216-11.993-30.012-19.61-42.64l50.986-23.86zm150.863 9.148c3.968-.006 6.814.03 9.35.065l10.396 32.3c-26.257-4.15-58.633-4.19-87.057-3.185-15.575.55-28.004 1.327-37.857 2.065l24.108-24.237.95-.23c23.746-5.79 59.86-6.746 80.11-6.778zm-42.192 47.39c24.924-.053 50.366 1.266 68.416 5.103l13.428 41.727c-38.708-8.23-88.984-10.977-132.086-11.738-27.222-.482-49.237-.188-63.87.173l30.8-30.963c7.765-.757 30.71-2.842 58.853-3.836 7.9-.28 16.15-.45 24.458-.467z"
                            />
                        </svg>
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Данные из Паутины
                    </Typography>
                </Box>

                <IconButton onClick={handleClose} size="small" sx={{ color: 'var(--text-secondary)', padding: { xs: '4px', md: '8px' } }}>
                    <CloseIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
                </IconButton>
            </DialogTitle>

            {loading && <LinearProgress sx={{ height: { xs: 1.5, md: 2 } }} />}

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: '8px', md: '32px' },
                padding: { xs: '8px 12px', md: '10px 16px' },
                backgroundColor: 'var(--background-default)',
                borderBottom: '1px solid var(--border-subtle)',
                flexWrap: 'wrap',
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, md: 1 }, width: { xs: '100%', md: 'auto' } }}>
                    {/* Выбор файла */}
                    <Box sx={{ display: 'flex', flex: 1 }}>
                        <Autocomplete
                            value={selectedFile}
                            onChange={handleFileSelect}
                            options={files}
                            getOptionLabel={(option) => `${option.name} (${formatFileDate(option.uploaded_at)})`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Выберите файл"
                                    size="small"
                                    sx={{
                                        width: { xs: '100%', md: '300px' }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <FileIcon sx={{ mr: { xs: 0.75, md: 1 }, color: 'var(--text-secondary)', fontSize: { xs: 18, md: 20 } }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            loading={loading}
                            disabled={loading}
                            noOptionsText="Нет файлов"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'var(--background-paper)',
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '10px', md: '11px' },
                                    padding: { xs: '4px 8px !important', md: '6px 10px !important' },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--border-default)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--primary-color)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--primary-color)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'var(--text-secondary)',
                                    fontSize: { xs: '10px', md: '11px' },
                                    '&.Mui-focused': {
                                        color: 'var(--primary-color)',
                                    },
                                },
                                '& .MuiAutocomplete-input': {
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '10px', md: '11px' },
                                    padding: { xs: '2px 8px', md: '6px 10px' },
                                },
                                '& .MuiAutocomplete-popupIndicator': {
                                    color: 'var(--text-secondary)',
                                },
                                '& .MuiAutocomplete-clearIndicator': {
                                    color: 'var(--text-secondary)',
                                },
                            }}
                            ListboxProps={{
                                sx: {
                                    backgroundColor: 'var(--background-paper)',
                                    border: '1px solid var(--border-default)',
                                    borderRadius: { xs: '6px', md: '8px' },
                                    '& .MuiAutocomplete-option': {
                                        color: 'var(--text-primary)',
                                        backgroundColor: 'var(--background-paper)',
                                        fontSize: { xs: '10px', md: '11px' },
                                        minHeight: { xs: 32, md: 36 },
                                        '&:hover': {
                                            backgroundColor: 'var(--hover-bg)',
                                        },
                                        '&[aria-selected="true"]': {
                                            backgroundColor: 'var(--selected-bg)',
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, width: 'auto', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
                        <input ref={fileInputRef} type="file" hidden accept=".xlsx" onChange={handleFileUpload} />

                        <Tooltip title="Загрузить файл">
                            <IconButton
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                sx={getActionButtonSx('success')}
                            >
                                {uploading ? <CircularProgress size={{ xs: 16, md: 18 }} /> : <CloudUploadIcon />}
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Скачать файл">
                            <IconButton
                                onClick={handleFileDownload}
                                disabled={!selectedFile}
                                sx={getActionButtonSx('primary')}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Обновить список">
                            <IconButton 
                                onClick={loadFiles} 
                                disabled={loading}
                                sx={getActionButtonSx('primary')}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>

                        {isAdmin && (
                            <Tooltip title="Удалить файл">
                                <IconButton
                                    onClick={handleFileDelete}
                                    disabled={!selectedFile || deleting}
                                    sx={getActionButtonSx('error')}
                                >
                                    {deleting ? <CircularProgress size={{ xs: 16, md: 18 }} /> : <DeleteIcon />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                {/* <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} /> */}
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, md: 2 }, width: { xs: '100%', md: 'auto' } }}>
                    {/* Поиск */}
                    <Box sx={{ display: 'flex', flex: 1 }}>
                        <TextField
                            size="small"
                            placeholder="Поиск..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                minWidth: { xs: '100%', md: 200 },
                                width: { xs: '100%', md: 'auto' },
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'var(--background-paper)',
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '10px', md: '11px' },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--border-default)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--primary-color)',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--primary-color)',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '10px', md: '11px' },
                                    padding: { xs: '6px 8px', md: '8px 10px' },
                                    '&::placeholder': {
                                        color: 'var(--text-secondary)',
                                        opacity: 1,
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: { xs: 0.75, md: 1 }, color: 'var(--text-secondary)', fontSize: { xs: 18, md: 20 } }} />,
                                endAdornment: searchTerm && (
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setSearchTerm('')} 
                                        sx={{ 
                                            padding: { xs: '4px', md: '8px' },
                                            color: 'var(--text-secondary)',
                                            '&:hover': {
                                                color: 'var(--text-primary)',
                                                backgroundColor: 'var(--hover-bg)',
                                            },
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Box>
                    {/* Меню управления столбцами */}
                    <Tooltip title="Управление столбцами">
                        <IconButton
                            onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                            sx={getActionButtonSx('primary')}
                        >
                            <ViewColumnIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Menu
                    anchorEl={columnsMenuAnchor}
                    open={Boolean(columnsMenuAnchor)}
                    onClose={() => setColumnsMenuAnchor(null)}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'var(--background-paper)',
                            border: '1px solid var(--border-default)',
                            boxShadow: 'var(--shadow-lg)',
                            borderRadius: { xs: '6px', md: '8px' },
                            maxHeight: { xs: '60vh', md: 400 },
                            minWidth: { xs: 200, md: 250 },
                        }
                    }}
                >
                    <Box sx={{ p: { xs: 0.75, md: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.75, md: 1 }, px: { xs: 0.75, md: 1 }, flexWrap: 'wrap', gap: { xs: 0.5, md: 0 } }}>
                            <Typography sx={{ fontSize: { xs: '11px', md: '12px' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                                Видимость столбцов
                            </Typography>
                            <Box sx={{ display: 'flex', gap: { xs: 0.4, md: 0.5 } }}>
                                <Button
                                    size="small"
                                    onClick={handleShowAllColumns}
                                    sx={{
                                        fontSize: { xs: '9px', md: '10px' },
                                        textTransform: 'none',
                                        color: 'var(--primary-color)',
                                        minWidth: 'auto',
                                        px: { xs: 0.75, md: 1 },
                                        padding: { xs: '2px 6px', md: '4px 8px' }
                                    }}
                                >
                                    Все
                                </Button>
                                <Button
                                    size="small"
                                    onClick={handleHideAllColumns}
                                    sx={{
                                        fontSize: { xs: '9px', md: '10px' },
                                        textTransform: 'none',
                                        color: 'var(--text-secondary)',
                                        minWidth: 'auto',
                                        px: { xs: 0.75, md: 1 },
                                        padding: { xs: '2px 6px', md: '4px 8px' }
                                    }}
                                >
                                    Скрыть
                                </Button>
                            </Box>
                        </Box>
                        <Divider sx={{ my: { xs: 0.4, md: 0.5 }, borderColor: 'var(--border-default)' }} />
                        {columns.map(column => (
                            <MenuItem
                                key={column.key}
                                onClick={() => handleColumnVisibilityChange(column.key)}
                                sx={{
                                    fontSize: { xs: '10px', md: '11px' },
                                    padding: { xs: '3px 6px', md: '4px 8px' },
                                    minHeight: { xs: 24, md: 28 },
                                    color: 'var(--text-primary)',
                                    '&:hover': {
                                        backgroundColor: 'var(--hover-bg)',
                                    },
                                }}
                            >
                                <Checkbox
                                    checked={visibleColumns[column.key] !== false}
                                    size="small"
                                    sx={{
                                        padding: { xs: '1px', md: '2px' },
                                        color: 'var(--text-secondary)',
                                        '&.Mui-checked': {
                                            color: 'var(--primary-color)',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: { xs: '16px', md: '18px' },
                                        },
                                    }}
                                />
                                <ListItemText
                                    primary={column.label}
                                    sx={{
                                        '& .MuiListItemText-primary': {
                                            fontSize: { xs: '10px', md: '11px' },
                                        }
                                    }}
                                />
                            </MenuItem>
                        ))}
                    </Box>
                </Menu>

            </Box>

            {/* Фильтры */}
            <Box sx={{ 
                py: { xs: 0.2, md: 1 }, 
                gap: { xs: 0.2, md: 1 }, 
                flexDirection: 'column',
                display: 'flex',
                alignItems: 'center',
                padding: { xs: '2px 12px', md: '10px 16px' },
                backgroundColor: 'var(--background-default)',
                borderBottom: '1px solid var(--border-subtle)',
                flexWrap: 'wrap',
            }}>
                {/* Заголовок фильтров с кнопкой сворачивания на мобильных */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: { xs: 0.2, md: 1 }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                        <FilterListIcon sx={{ color: 'var(--text-secondary)', fontSize: { xs: 16, md: 20 } }} />
                        <Typography sx={{ fontSize: { xs: '10px', md: '12px' }, fontWeight: 600, color: 'var(--text-secondary)' }}>
                            Фильтры:
                        </Typography>
                        {isMobile && !filtersExpanded && (selectedProblems.length > 0 || selectedTags.length > 0 || selectedClasses.length > 0) && (
                            <Chip
                                size="small"
                                label={selectedProblems.length + selectedTags.length + selectedClasses.length}
                                sx={{
                                    height: { xs: 18, md: 20 },
                                    fontSize: { xs: '9px', md: '10px' },
                                    backgroundColor: 'var(--primary-color)',
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            />
                        )}
                    </Box>
                    
                    {/* Кнопка сворачивания только на мобильных */}
                    <IconButton
                        size="small"
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            padding: '4px',
                            color: 'var(--text-secondary)',
                            '&:hover': {
                                backgroundColor: 'var(--hover-bg)',
                                color: 'var(--text-primary)'
                            }
                        }}
                    >
                        {filtersExpanded ? 
                            <ExpandLessIcon sx={{ fontSize: 18 }} /> : 
                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                        }
                    </IconButton>
                </Box>

                {/* Содержимое фильтров с анимацией сворачивания */}
                <Collapse in={filtersExpanded || !isMobile} timeout="auto" unmountOnExit={false} sx={{
                    width: '100%'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        gap: { xs: 0.75, md: 1 },
                        width: '100%'
                    }}>
                        {/* Фильтр по проблемам */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: { xs: 0.3, md: 0.5 }, 
                            flexWrap: 'wrap', 
                            // width: '100%' 
                        }}>
                            {problemFilters.map(filter => (
                                <Chip
                                    key={filter.key}
                                    size="small"
                                    icon={<filter.icon />}
                                    label={`${filter.label} (${stats[filter.key]})`}
                                    onClick={() => {
                                        setSelectedProblems(prev =>
                                            prev.includes(filter.key)
                                                ? prev.filter(p => p !== filter.key)
                                                : [...prev, filter.key]
                                        );
                                    }}
                                    sx={getFilterChipSx(selectedProblems.includes(filter.key) ? 1 : 0)}
                                />
                            ))}
                        </Box>

                        {/* <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} /> */}

                        {/* Селекты и кнопка очистки в одной строке на мобильном */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: { xs: 0.5, md: 1 }, 
                            // width: '100%',
                            alignItems: 'center',
                            flexWrap: { xs: 'nowrap', md: 'wrap' }
                        }}>
                            {/* Фильтр по тэгам */}
                            <FormControl size="small" sx={{ 
                                minWidth: { xs: 0, md: 150 }, 
                                width: { xs: '1 1 auto', md: 'auto' },
                                flex: { xs: 1, md: 'none' }
                            }}>
                                <InputLabel sx={{
                                    color: 'var(--text-secondary)',
                                    fontSize: { xs: '9px', md: '11px' },
                                    '&.Mui-focused': { color: 'var(--primary-color)' }
                                }}>
                                    Тэги
                                </InputLabel>
                                <Select
                                    multiple
                                    value={selectedTags}
                                    onChange={(e) => setSelectedTags(e.target.value)}
                                    input={<OutlinedInput label="Тэги" />}
                                    renderValue={(selected) => selected.length > 0 ? `${selected.length}` : ''}
                                    sx={{
                                        backgroundColor: 'var(--background-paper)',
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '9px', md: '11px' },
                                        height: { xs: 28, md: 32 },
                                        borderRadius: { xs: '4px', md: '6px' },
                                        '& .MuiOutlinedInput-input': {
                                            padding: { xs: '4px 24px 4px 8px', md: '6px 32px 6px 12px' },
                                            fontSize: { xs: '9px', md: '11px' },
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--border-default)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--primary-color)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--primary-color)',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: 'var(--text-secondary)',
                                            fontSize: { xs: '14px', md: '18px' },
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: 'var(--background-paper)',
                                                border: '1px solid var(--border-default)',
                                                boxShadow: 'var(--shadow-lg)',
                                                borderRadius: { xs: '6px', md: '8px' },
                                                '& .MuiMenuItem-root': {
                                                    fontSize: { xs: '10px', md: '11px' },
                                                    padding: { xs: '2px 4px', md: '2px 6px' },
                                                    minHeight: { xs: 20, md: 24 },
                                                    color: 'var(--text-primary)',
                                                    '&:hover': {
                                                        backgroundColor: 'var(--hover-bg)',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'var(--selected-bg)',
                                                        '&:hover': {
                                                            backgroundColor: 'var(--selected-bg)',
                                                        },
                                                    },
                                                },
                                                '& .MuiCheckbox-root': {
                                                    padding: { xs: '1px', md: '2px' },
                                                    color: 'var(--text-secondary)',
                                                    '&.Mui-checked': {
                                                        color: 'var(--primary-color)',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: { xs: '16px', md: '18px' },
                                                    },
                                                },
                                                '& .MuiListItemText-primary': {
                                                    fontSize: { xs: '10px', md: '11px' },
                                                    color: 'var(--text-primary)',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {uniqueTags.map(tag => (
                                        <MenuItem key={tag} value={tag}>
                                            <Checkbox checked={selectedTags.includes(tag)} size="small" />
                                            <ListItemText primary={tag} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Фильтр по классам */}
                            <FormControl size="small" sx={{ 
                                minWidth: { xs: 0, md: 150 }, 
                                width: { xs: '1 1 auto', md: 'auto' },
                                flex: { xs: 1, md: 'none' }
                            }}>
                                <InputLabel sx={{
                                    color: 'var(--text-secondary)',
                                    fontSize: { xs: '9px', md: '11px' },
                                    '&.Mui-focused': { color: 'var(--primary-color)' }
                                }}>
                                    Классы
                                </InputLabel>
                                <Select
                                    multiple
                                    value={selectedClasses}
                                    onChange={(e) => setSelectedClasses(e.target.value)}
                                    input={<OutlinedInput label="Классы" />}
                                    renderValue={(selected) => selected.length > 0 ? `${selected.length}` : ''}
                                    sx={{
                                        backgroundColor: 'var(--background-paper)',
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '9px', md: '11px' },
                                        height: { xs: 28, md: 32 },
                                        borderRadius: { xs: '4px', md: '6px' },
                                        '& .MuiOutlinedInput-input': {
                                            padding: { xs: '4px 24px 4px 8px', md: '6px 32px 6px 12px' },
                                            fontSize: { xs: '9px', md: '11px' },
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--border-default)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--primary-color)',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--primary-color)',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: 'var(--text-secondary)',
                                            fontSize: { xs: '14px', md: '18px' },
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: 'var(--background-paper)',
                                                border: '1px solid var(--border-default)',
                                                boxShadow: 'var(--shadow-lg)',
                                                borderRadius: { xs: '6px', md: '8px' },
                                                '& .MuiMenuItem-root': {
                                                    fontSize: { xs: '10px', md: '11px' },
                                                    padding: { xs: '2px 4px', md: '2px 6px' },
                                                    minHeight: { xs: 20, md: 24 },
                                                    color: 'var(--text-primary)',
                                                    '&:hover': {
                                                        backgroundColor: 'var(--hover-bg)',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'var(--selected-bg)',
                                                        '&:hover': {
                                                            backgroundColor: 'var(--selected-bg)',
                                                        },
                                                    },
                                                },
                                                '& .MuiCheckbox-root': {
                                                    padding: { xs: '1px', md: '2px' },
                                                    color: 'var(--text-secondary)',
                                                    '&.Mui-checked': {
                                                        color: 'var(--primary-color)',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: { xs: '16px', md: '18px' },
                                                    },
                                                },
                                                '& .MuiListItemText-primary': {
                                                    fontSize: { xs: '10px', md: '11px' },
                                                    color: 'var(--text-primary)',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {uniqueClasses.map(cls => (
                                        <MenuItem key={cls} value={cls}>
                                            <Checkbox checked={selectedClasses.includes(cls)} size="small" />
                                            <ListItemText primary={cls} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Сброс фильтров */}
                            {(selectedProblems.length > 0 || selectedTags.length > 0 || selectedClasses.length > 0) && (
                                <Tooltip title="Сбросить фильтры">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSelectedProblems([]);
                                            setSelectedTags([]);
                                            setSelectedClasses([]);
                                        }}
                                        sx={{ 
                                            padding: { xs: '3px', md: '8px' },
                                            color: 'var(--text-secondary)',
                                            flexShrink: 0,
                                            '&:hover': {
                                                color: 'var(--text-primary)',
                                                backgroundColor: 'var(--hover-bg)',
                                            },
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: { xs: 14, md: 18 } }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            </Box>

            <DialogContent sx={{
                padding: 0,
                backgroundColor: 'var(--background-default)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {fileData.length === 0 && !loading ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        gap: 2,
                        p: 4,
                    }}>
                        <FileIcon sx={{ fontSize: 64, color: 'var(--text-secondary)' }} />
                        <Typography sx={{ color: 'var(--text-secondary)' }}>
                            {files.length === 0 ? 'Нет загруженных файлов' : 'Выберите файл для просмотра'}
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Загрузить файл
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* Оверлей загрузки */}
                        {loading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(var(--background-paper), 0.8)',
                                backdropFilter: 'blur(2px)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                gap: 2,
                            }}>
                                <CircularProgress size={48} sx={{ color: 'var(--primary-color)' }} />
                                <Typography sx={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    Загрузка данных...
                                </Typography>
                            </Box>
                        )}

                        <TableContainer 
                            component={Paper} 
                            elevation={0}
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                margin: { xs: '0 8px 8px 8px', md: '0 12px 12px 12px' },
                                backgroundColor: 'var(--background-paper)',
                                border: '1px solid var(--border-default)',
                                borderRadius: { xs: '6px', md: '8px' },
                                '& .MuiTable-root': {
                                    borderCollapse: 'collapse',
                                },
                                '&::-webkit-scrollbar': {
                                    width: { xs: '4px', md: '6px' },
                                    height: { xs: '4px', md: '6px' }
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'var(--background-subtle)',
                                    borderRadius: '3px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'var(--border-default)',
                                    borderRadius: '3px',
                                    '&:hover': { background: 'var(--text-muted)' }
                                },
                            }}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead sx={{
                                    '& .MuiTableCell-head': {
                                        backgroundColor: 'var(--table-header-bg)',
                                        color: 'var(--text-secondary)',
                                        fontWeight: 600,
                                        fontSize: { xs: '9px', md: '11px' },
                                        padding: { xs: '6px 8px', md: '10px 12px' },
                                        borderBottom: '1px solid var(--border-default)',
                                        whiteSpace: 'nowrap',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                    },
                                }}>
                                    <TableRow>
                                        {visibleColumnsList.map(column => (
                                            <SortableHeader
                                                key={column.key}
                                                column={column}
                                                sortConfig={sortConfig}
                                                onSort={handleSort}
                                            />
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {processedData.map(item => (
                                        <TableRow 
                                            key={item.id}
                                            sx={{
                                                transition: 'background-color 0.15s ease',
                                                backgroundColor: 'var(--table-row-bg)',
                                                '&:hover': {
                                                    backgroundColor: 'var(--table-row-hover)',
                                                },
                                            }}
                                        >
                                            {visibleColumnsList.map(column => {
                                                // Столбцы, которые нужно выровнять по центру
                                                const centeredColumns = ['complexName', 'complex', 'tag', 'status', 'poverka', 'poverka_from', 'poverka_to', 'performance', 'speed'];
                                                const isCentered = centeredColumns.includes(column.key);

                                                return (
                                                    <TableCell
                                                        key={column.key}
                                                        sx={{
                                                            fontSize: { xs: '9px', md: '11px' },
                                                            padding: { xs: '6px 8px', md: '10px 12px' },
                                                            color: 'var(--text-primary)',
                                                            borderBottom: '1px solid var(--table-border)',
                                                            borderRight: '1px solid var(--table-border)',
                                                            '&:last-child': {
                                                                borderRight: 'none',
                                                            },
                                                            ...(isCentered ? { textAlign: 'center' } : {}),
                                                        }}
                                                    >
                                                        {renderCell(item, column)}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                padding: { xs: '6px 8px', md: '12px 16px' },
                backgroundColor: 'var(--background-default)',
                borderTop: '1px solid var(--border-default)',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: { xs: '4px', md: '12px' },
                flexDirection: { xs: 'row', md: 'row' },
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, md: 2 }, 
                    alignItems: 'center', 
                    flexWrap: 'wrap',
                    flex: 1,
                    minWidth: 0
                }}>
                    <Typography sx={{ 
                        fontSize: { xs: '9px', md: '12px' }, 
                        fontWeight: 600, 
                        color: 'var(--text-secondary)',
                        whiteSpace: 'nowrap'
                    }}>
                        Записей: {processedData.length} из {fileData.length}
                    </Typography>

                    {stats.expired > 0 && (
                        <Chip 
                            status="error" 
                            size="small" 
                            label={`Истекших: ${stats.expired}`}
                            sx={getStatusChipSx('error')}
                        />
                    )}
                    {stats.expiring > 0 && (
                        <Chip 
                            status="warning" 
                            size="small" 
                            label={`Истекает: ${stats.expiring}`}
                            sx={getStatusChipSx('warning')}
                        />
                    )}
                    {stats.notApproved > 0 && (
                        <Chip 
                            status="warning" 
                            size="small" 
                            label={`Не утверждён: ${stats.notApproved}`}
                            sx={getStatusChipSx('warning')}
                        />
                    )}
                    {stats.notWorking > 0 && (
                        <Chip 
                            status="error" 
                            size="small" 
                            label={`Не работает: ${stats.notWorking}`}
                            sx={getStatusChipSx('error')}
                        />
                    )}
                    {stats.emptyViolations > 0 && (
                        <Chip 
                            status="warning" 
                            size="small" 
                            label={`Нет нарушений: ${stats.emptyViolations}`}
                            sx={getStatusChipSx('warning')}
                        />
                    )}
                    {stats.activeSchedule > 0 && (
                        <Chip 
                            status="info" 
                            size="small" 
                            icon={<ScheduleIcon />} 
                            label={`Активных: ${stats.activeSchedule}`}
                            sx={getStatusChipSx('info')}
                        />
                    )}

                    {stats.expired === 0 && stats.expiring === 0 && stats.notApproved === 0 && stats.notWorking === 0 && stats.emptyViolations === 0 && fileData.length > 0 && (
                        <Chip 
                            status="success" 
                            size="small" 
                            icon={<CheckCircleIcon />} 
                            label="Проблем нет"
                            sx={getStatusChipSx('success')}
                        />
                    )}
                </Box>

                <Button
                    variant="contained"
                    onClick={handleClose}
                    sx={{
                        p: { xs: '4px 8px', md: '2px 10px' },
                        textTransform: 'none',
                        borderRadius: 'var(--border-radius-medium)',
                        fontSize: { xs: '10px', md: '14px' },
                        minWidth: { xs: 'auto', md: 'auto' },
                    }}
                >
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PautinaDialog;
